import {
  keyFinancialRatioOptions,
  netSavingsTypeOptions,
  valueSourceOptions,
  valueTypeOptions
} from '@unistocks/features/dashboard/utils';
import {
  CalculatedValueDto,
  KeyFinancialRatio,
  NetSavingsType,
  ValueSource,
  ValueType
} from '@unistocks/proxy/axios-client';
import { t } from '@unistocks/shared/localization/index.ts';
import { combineFlags, hasFlag, sortFn } from '@unistocks/shared/utils/util';
import { Option, SelectOption } from '@unistocks/types';

/**
 * Get all value type select options
 */
export function getDashboardValueTypes(source?: ValueSource): Option<ValueType>[] {
  const items: Array<SelectOption<ValueType>> = [];
  const order = [
    ValueType.TotalValue,
    ValueType.NominalChange,
    ValueType.PercentageChange,
    ValueType.NetSavings,
    ValueType.KeyFinancialRatio,
    ValueType.RelativeValue
  ];

  for (const { key, value } of valueTypeOptions) {
    if (source && !sourceHasType(source, value)) {
      continue;
    }

    items.push({
      key: t(key),
      value
    });
  }

  return items.sort((a, b) => sortFn(order.indexOf(a.value), order.indexOf(b.value)));
}

/**
 * Get all valid value sources for a specific value type
 */
export function getDashboardValueSources(type?: ValueType): SelectOption<ValueSource>[] {
  const items: Array<SelectOption<ValueSource>> = [];
  const order: ValueSource[] = [
    ValueSource.Portfolio,
    ValueSource.SharedPortfolio,
    ValueSource.Asset,
    ValueSource.ExchangeRate
  ];

  for (const { key, value } of valueSourceOptions) {
    if (type && !typeHasSource(type, value)) {
      continue;
    }

    items.push({
      key: t(key),
      value
    });
  }

  return items.sort(({ value: valueA }, { value: valueB }) => {
    return order.indexOf(valueA) - order.indexOf(valueB);
  });
}

export const sourceHasType = (source: ValueSource, type: ValueType): boolean => {
  let validTypes: number;

  switch (source) {
    case ValueSource.Portfolio:
      validTypes = ValueType.All;
      break;

    case ValueSource.SharedPortfolio:
      validTypes = combineFlags([
        ValueType.TotalValue,
        ValueType.NominalChange,
        ValueType.PercentageChange
      ]);
      break;

    case ValueSource.Asset:
      validTypes = combineFlags([
        ValueType.TotalValue,
        ValueType.NominalChange,
        ValueType.PercentageChange
      ]);
      break;

    case ValueSource.ExchangeRate:
      validTypes = combineFlags([
        ValueType.TotalValue,
        ValueType.NominalChange,
        ValueType.PercentageChange
      ]);
      break;

    default:
      return false;
  }

  return hasFlag(validTypes, type);
};

/**
 * Check if the value source is valid for the selected value type
 */
export function typeHasSource(valueType: ValueType, valueSource: ValueSource): boolean {
  let validSources: ValueSource[];

  switch (valueType) {
    case ValueType.RelativeValue:
    case ValueType.TotalValue:
      validSources = [
        ValueSource.Portfolio,
        ValueSource.SharedPortfolio,
        ValueSource.ExchangeRate,
        ValueSource.Asset
      ];
      break;

    case ValueType.NominalChange:
    case ValueType.PercentageChange:
      validSources = [ValueSource.Portfolio, ValueSource.SharedPortfolio, ValueSource.Asset];
      break;

    case ValueType.NetSavings:
    case ValueType.KeyFinancialRatio:
      validSources = [ValueSource.Portfolio];
      break;

    default:
      return false;
  }

  return validSources.includes(valueSource);
}

/**
 * Get all net savings type select options
 */
export function getNetSavingsTypeSelectOptions(): Array<Option<NetSavingsType>> {
  const items: Array<Option<NetSavingsType>> = [];

  for (const { key, value } of netSavingsTypeOptions) {
    items.push({
      key: t(key),
      value
    });
  }

  return items;
}

/**
 * Get all key financial ratio select options
 */
export function getKeyFinancialRatioSelectOptions(): Array<Option<KeyFinancialRatio>> {
  const items: Array<Option<KeyFinancialRatio>> = [];

  for (const { key, value } of keyFinancialRatioOptions) {
    items.push({
      key: t(key),
      value
    });
  }

  return items;
}

export function getEmptyCalculatedValue(): CalculatedValueDto {
  return {
    displayName: '',
    valueType: ValueType.TotalValue,
    valueSource: ValueSource.Portfolio,
    nestedValues: null,
    params: {
      portfolios: [],
      sharedPortfolios: [],
      sections: [],
      assetTypes: [],
      period: null,
      assetSearch: null,
      fromCurrency: null,
      toCurrency: null,
      assetCollection: null,
      assets: null,
      keyFinancialRatio: null,
      netSavingsType: null
    }
  };
}

export function getDefaultNestedValue(): CalculatedValueDto {
  return {
    valueType: ValueType.TotalValue,
    valueSource: ValueSource.Portfolio,
    displayName: null,
    nestedValues: null,
    params: {
      portfolios: [],
      sharedPortfolios: [],
      sections: [],
      assetTypes: [],
      assetCollection: null,
      assets: null,
      assetSearch: null,
      fromCurrency: null,
      keyFinancialRatio: null,
      netSavingsType: null,
      period: null,
      toCurrency: null
    }
  };
}
